import { BundleFeature, BundleFeatureCategory, BundleFeatureValue } from 'src/api/contentful/contentTypes.cloverdotcom';
import { FlatEntry } from 'src/services/cms/flattenContentful';
import { Bundles, FeaturesByCategories, FeaturesByCategory } from './types';

/**
 * Function to get or create a specific category inside the structure
 * @param {FeaturesByCategories} featuresByCategories Collection of categories of features
 * @param {BundleFeatureCategory} bundleFeatureCategory A Contentful formated Bundle Feature Category
 * @returns {FeaturesByCategories} A reference of found or created new Category inside the Present structure
 */
function getOrCreateCategory(
  featuresByCategories: FeaturesByCategories,
  bundleFeatureCategory: FlatEntry<BundleFeatureCategory>
): FeaturesByCategory | undefined {
  const isExistingCategory = featuresByCategories.findIndex(category => category.id === bundleFeatureCategory.id) >= 0;

  if (!isExistingCategory) {
    featuresByCategories.push({ ...bundleFeatureCategory, features: [] });
  }

  return featuresByCategories.find(category => category.id === bundleFeatureCategory.id);
}

/**
 * Function to add a value to a new or existing Feature
 * @param {FeaturesByCategory} category A reference of Category inside the Present structure
 * @param {BundleFeature} currentFeature A Contentful format Bundle Feature Category
 * @param {BundleFeatureValue} value A Contentful format Bundle Feature Value
 * @param {string} bundleId The Bundle id that this new value will be related to
 */
function addValueToExistingOrNewFeature(
  category: FeaturesByCategory,
  currentFeature: FlatEntry<BundleFeature>,
  value: FlatEntry<BundleFeatureValue>,
  bundleId: string
): void {
  const featureIndex = category.features.findIndex(feature => feature.id === currentFeature.id);
  const isExistingFeature = featureIndex >= 0;

  const { bundleFeature, ...cleanedValue } = value;

  if (!isExistingFeature) {
    category.features.push({ ...currentFeature, values: [{ ...cleanedValue, bundleId }] });
  } else {
    category.features[featureIndex].values.push({ ...cleanedValue, bundleId });
  }
}

/**
 * Function to decorate Features to present within the Bundles on the view
 * @param {Bundles} bundles A Contentful format of Bundles
 * @returns {FeaturesByCategories} The present structure of Bundles on topdown,
 * from catetories to value for each Bundle, referenced by bundle id
 */
export function decorateBundlesFeatures(bundles: Bundles): FeaturesByCategories {
  const featuresByCategories: FeaturesByCategories = [];

  bundles.forEach(bundle => {
    bundle.featureValues?.forEach(value => {
      if (!bundle.id || !value.bundleFeature) return;

      const { bundleFeatureCategory, ...currentFeature } = value.bundleFeature;

      if (!bundleFeatureCategory) return;

      const selectedCategory = getOrCreateCategory(featuresByCategories, bundleFeatureCategory);

      if (!selectedCategory) return;

      addValueToExistingOrNewFeature(
        selectedCategory,
        currentFeature,
        value,
        bundle.id
      );
    });
  });

  return featuresByCategories;
}

/**
 * Function to filter Bundles that have Feature Values
 * @param {Bundles} bundles A Contentful format of Bundles
 * @returns {Bundles} Bundles that have featureValues set
 */
export function filterBundlesWithFeatureValues(bundles: Bundles): Bundles {
  return bundles.filter(bundle => bundle.featureValues && bundle.featureValues.length);
}
